import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  People as PeopleIcon,
  Delete as DeleteIcon,
  Summarize as ResumenIcon,
  FileDownload as ExportIcon,
  CheckCircle as AprobarIcon,
  Lock as CerrarIcon,
  Undo as RevertirIcon,
  Email as EmailIcon,
  AccountBalance as BancoIcon,
} from '@mui/icons-material';
import { http } from '../../../../api/http';
import ResumenPlanillaDialog from './ResumenPlanillaDialog';
import { usePlanillonStore } from '../store';
import { ESTADOS_PLANILLA } from '../types/planillon';
import { MESES } from '../../periodos/types/periodo';

interface EnvioMasivoResult {
  totalTrabajadores: number;
  enviadas: number;
  sinCorreo: number;
  errores: number;
  trabajadoresSinCorreo: string[];
  trabajadoresError: string[];
}

const PlanillonToolbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [resumenOpen, setResumenOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<EnvioMasivoResult | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Pago Bancario state
  const [bancoDialogOpen, setBancoDialogOpen] = useState(false);
  const [bancoPreview, setBancoPreview] = useState<any>(null);
  const [bancoLoading, setBancoLoading] = useState(false);
  const [bancoSelected, setBancoSelected] = useState('BCP');
  const [bancoCuentaCargo, setBancoCuentaCargo] = useState('');
  const [bancoGenerando, setBancoGenerando] = useState(false);

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmColor, setConfirmColor] = useState<'primary' | 'error' | 'warning' | 'success'>('primary');

  const {
    periodos,
    planillas,
    selectedPeriodoId,
    selectedPlanilla,
    planillonData,
    calculating,
    fetchPlanillasByPeriodo,
    fetchPlanillon,
    setSelectedPlanilla,
    generarDetalle,
    calcularPlanilla,
    cambiarEstado,
    deletePlanilla,
    openDialog,
  } = usePlanillonStore();

  const askConfirm = useCallback((message: string, action: () => void, color: 'primary' | 'error' | 'warning' | 'success' = 'primary') => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmColor(color);
    setConfirmOpen(true);
  }, []);

  const handleConfirm = () => {
    confirmAction?.();
    setConfirmOpen(false);
  };

  const handlePeriodoChange = (idPeriodo: number) => {
    fetchPlanillasByPeriodo(idPeriodo);
    setSelectedPlanilla(null);
  };

  const handlePlanillaChange = (idPlanilla: number) => {
    const planilla = planillas.find((p) => p.idPlanilla === idPlanilla);
    if (planilla) {
      setSelectedPlanilla(planilla);
      fetchPlanillon(idPlanilla);
    }
  };

  const handleGenerarDetalle = () => {
    if (!selectedPlanilla) return;
    askConfirm(
      '¿Generar detalle para todos los trabajadores activos? Esto reemplazará cualquier detalle existente.',
      () => generarDetalle(selectedPlanilla.idPlanilla),
    );
  };

  const handleCalcular = () => {
    if (!selectedPlanilla) return;
    askConfirm(
      '¿Calcular la planilla completa? Los montos anteriores serán reemplazados.',
      () => calcularPlanilla(selectedPlanilla.idPlanilla),
      'primary',
    );
  };

  const handleExportCSV = () => {
    if (!planillonData || !selectedPlanilla) return;

    const { columnas, filas } = planillonData;

    // Headers
    const headers = [
      'Trabajador',
      'Documento',
      'Area',
      'Días Lab.',
      ...columnas.map((c) => c.descripcion),
      'Total Rem.',
      'Total Desc.',
      'Total Aport.',
      'Neto a Pagar',
    ];

    // Rows
    const csvRows = filas.map((row) => [
      `"${row.nombreTrabajador}"`,
      row.documento,
      `"${row.area}"`,
      row.diasLaborados,
      ...columnas.map((c) => (row.conceptoMontos?.[c.idConcepto] ?? 0).toFixed(2)),
      row.totalRemuneraciones.toFixed(2),
      row.totalDescuentos.toFixed(2),
      row.totalAportes.toFixed(2),
      row.netoPagar.toFixed(2),
    ]);

    const csvContent = [headers.join(','), ...csvRows.map((r) => r.join(','))].join('\n');

    // BOM for Excel UTF-8 compatibility
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `planilla_${selectedPlanilla.descripcion.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenEmailDialog = () => {
    setEmailResult(null);
    setEmailError(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmailResult(null);
    setEmailError(null);
  };

  const handleSendBoletas = async () => {
    if (!selectedPlanilla) return;

    setEmailSending(true);
    setEmailResult(null);
    setEmailError(null);

    try {
      const response = await http.post<EnvioMasivoResult>(
        `/planilla/reporte/boleta/${selectedPlanilla.idPlanilla}/envio-masivo`
      );
      setEmailResult(response.data);
    } catch (err: any) {
      setEmailError(err.response?.data || 'Error al enviar las boletas. Verifique la configuración SMTP.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleOpenBancoDialog = async () => {
    if (!selectedPlanilla) return;
    setBancoDialogOpen(true);
    setBancoLoading(true);
    setBancoPreview(null);
    try {
      const response = await http.get(`/planilla/pago-bancario/preview/${selectedPlanilla.idPlanilla}`);
      setBancoPreview(response.data);
    } catch {
      setBancoPreview(null);
    } finally {
      setBancoLoading(false);
    }
  };

  const handleGenerarArchivoBanco = async () => {
    if (!selectedPlanilla) return;
    setBancoGenerando(true);
    try {
      const fecha = new Date().toISOString().substring(0, 10).replace(/-/g, '');
      const response = await http.get(
        `/planilla/pago-bancario/generar/${selectedPlanilla.idPlanilla}`,
        {
          params: { banco: bancoSelected, cuentaCargo: bancoCuentaCargo, fechaCargo: fecha },
          responseType: 'blob',
        }
      );
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch
        ? filenameMatch[1].replace(/['"]/g, '')
        : `${bancoSelected}_HABERES.txt`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { /* handled by interceptor */ }
    finally {
      setBancoGenerando(false);
    }
  };

  const estadoConfig = selectedPlanilla
    ? ESTADOS_PLANILLA.find((e) => e.value === selectedPlanilla.estado)
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap' }}>
      {/* Selector de Periodo */}
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
        <InputLabel>Periodo</InputLabel>
        <Select
          value={selectedPeriodoId || ''}
          label="Periodo"
          onChange={(e) => handlePeriodoChange(Number(e.target.value))}
        >
          {periodos.map((p) => {
            const mes = MESES.find((m) => m.value === p.mes);
            return (
              <MenuItem key={p.idPeriodo} value={p.idPeriodo}>
                {mes?.label || p.mes} {p.anio} ({p.estado})
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Selector de Planilla */}
      <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 250 } }}>
        <InputLabel>Planilla</InputLabel>
        <Select
          value={selectedPlanilla?.idPlanilla || ''}
          label="Planilla"
          onChange={(e) => handlePlanillaChange(Number(e.target.value))}
          disabled={!selectedPeriodoId}
        >
          {planillas.map((p) => (
            <MenuItem key={p.idPlanilla} value={p.idPlanilla}>
              {p.descripcion} [{p.estado}]
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Estado badge */}
      {selectedPlanilla && estadoConfig && (
        <Chip
          label={estadoConfig.label}
          color={estadoConfig.color}
          variant="outlined"
          size="small"
        />
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Acciones */}
      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        startIcon={<AddIcon />}
        onClick={openDialog}
        disabled={!selectedPeriodoId}
      >
        {!isMobile && 'Nueva Planilla'}
      </Button>

      {selectedPlanilla && selectedPlanilla.estado === 'BORRADOR' && (
        <Button
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => askConfirm(
            `¿Eliminar la planilla "${selectedPlanilla.descripcion}"? Esta acción no se puede deshacer.`,
            () => deletePlanilla(selectedPlanilla.idPlanilla),
            'error',
          )}
        >
          {!isMobile && 'Eliminar'}
        </Button>
      )}

      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        color="secondary"
        startIcon={calculating ? <CircularProgress size={16} /> : <PeopleIcon />}
        onClick={handleGenerarDetalle}
        disabled={!selectedPlanilla || selectedPlanilla.estado !== 'BORRADOR' || calculating}
      >
        {!isMobile && 'Generar Detalle'}
      </Button>

      <Button
        variant="contained"
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
        onClick={handleCalcular}
        disabled={!selectedPlanilla || selectedPlanilla.estado === 'CERRADA' || calculating}
      >
        {!isMobile && 'Calcular'}
      </Button>

      {/* Flujo de estados */}
      {selectedPlanilla && selectedPlanilla.estado === 'CALCULADA' && (
        <>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            color="success"
            startIcon={<AprobarIcon />}
            onClick={() => askConfirm(
              '¿Aprobar esta planilla? Una vez aprobada, no podrá modificar los detalles.',
              () => cambiarEstado(selectedPlanilla.idPlanilla, 'APROBADA'),
              'success',
            )}
          >
            {!isMobile && 'Aprobar'}
          </Button>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<RevertirIcon />}
            onClick={() => askConfirm(
              '¿Revertir a Borrador? Podrá modificar los detalles nuevamente.',
              () => cambiarEstado(selectedPlanilla.idPlanilla, 'BORRADOR'),
            )}
          >
            {!isMobile && 'Revertir'}
          </Button>
        </>
      )}

      {selectedPlanilla && selectedPlanilla.estado === 'APROBADA' && (
        <>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            color="warning"
            startIcon={<CerrarIcon />}
            onClick={() => askConfirm(
              '¿Cerrar esta planilla? Esta acción es irreversible.',
              () => cambiarEstado(selectedPlanilla.idPlanilla, 'CERRADA'),
              'warning',
            )}
          >
            {!isMobile && 'Cerrar'}
          </Button>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<RevertirIcon />}
            onClick={() => askConfirm(
              '¿Revertir a Calculada? Podrá recalcular la planilla.',
              () => cambiarEstado(selectedPlanilla.idPlanilla, 'CALCULADA'),
            )}
          >
            {!isMobile && 'Revertir'}
          </Button>
        </>
      )}

      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        startIcon={<ResumenIcon />}
        onClick={() => setResumenOpen(true)}
        disabled={!selectedPlanilla || selectedPlanilla.estado === 'BORRADOR'}
      >
        {!isMobile && 'Resumen'}
      </Button>

      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        startIcon={<ExportIcon />}
        onClick={handleExportCSV}
        disabled={!planillonData || planillonData.filas.length === 0}
      >
        {!isMobile && 'Exportar CSV'}
      </Button>

      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        color="info"
        startIcon={<EmailIcon />}
        onClick={handleOpenEmailDialog}
        disabled={!selectedPlanilla || selectedPlanilla.estado === 'BORRADOR' || !planillonData || planillonData.filas.length === 0}
      >
        {!isMobile && 'Enviar Boletas'}
      </Button>

      <Button
        variant="outlined"
        size={isMobile ? 'small' : 'medium'}
        color="success"
        startIcon={<BancoIcon />}
        onClick={handleOpenBancoDialog}
        disabled={!selectedPlanilla || selectedPlanilla.estado === 'BORRADOR'}
      >
        {!isMobile && 'Pago Banco'}
      </Button>

      {/* Dialog de Confirmación */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar acción</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color={confirmColor} onClick={handleConfirm} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Enviar Boletas por Email */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Boletas por Email</DialogTitle>
        <DialogContent>
          {!emailResult && !emailError && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              Se enviará la boleta de pago a cada trabajador a su correo electrónico registrado.
              Los trabajadores sin correo registrado serán omitidos.
            </Alert>
          )}

          {emailResult && (
            <Box sx={{ mt: 1 }}>
              <Alert severity={emailResult.enviadas > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                <strong>Resultado del envío masivo:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>Total trabajadores: {emailResult.totalTrabajadores}</li>
                  <li>Boletas enviadas: {emailResult.enviadas}</li>
                  {emailResult.sinCorreo > 0 && <li>Sin correo registrado: {emailResult.sinCorreo}</li>}
                  {emailResult.errores > 0 && <li>Errores: {emailResult.errores}</li>}
                </ul>
              </Alert>

              {emailResult.trabajadoresSinCorreo.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <strong>Trabajadores sin correo:</strong>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {emailResult.trabajadoresSinCorreo.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </Alert>
              )}

              {emailResult.trabajadoresError.length > 0 && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <strong>Errores al enviar:</strong>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                    {emailResult.trabajadoresError.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </Alert>
              )}
            </Box>
          )}

          {emailError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {emailError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailSending}>
            {emailResult ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!emailResult && (
            <Button
              onClick={handleSendBoletas}
              variant="contained"
              color="primary"
              disabled={emailSending}
              startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            >
              {emailSending ? 'Enviando...' : 'Enviar a Todos'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Resumen */}
      {selectedPlanilla && (
        <ResumenPlanillaDialog
          open={resumenOpen}
          onClose={() => setResumenOpen(false)}
          idPlanilla={selectedPlanilla.idPlanilla}
          descripcionPlanilla={selectedPlanilla.descripcion}
        />
      )}

      {/* Dialog Pago Bancario */}
      <Dialog open={bancoDialogOpen} onClose={() => setBancoDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pago Bancario - Archivo de Transferencia</DialogTitle>
        <DialogContent>
          {bancoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : bancoPreview ? (
            <>
              {/* Resumen */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2, mt: 1, flexWrap: 'wrap' }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Periodo</Typography>
                  <Typography variant="h6" fontWeight="bold">{bancoPreview.periodo}</Typography>
                </Paper>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Trabajadores</Typography>
                  <Typography variant="h6" fontWeight="bold">{bancoPreview.totalTrabajadores}</Typography>
                </Paper>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Con cuenta</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">{bancoPreview.trabajadoresConCuenta}</Typography>
                </Paper>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Sin cuenta</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">{bancoPreview.trabajadoresSinCuenta}</Typography>
                </Paper>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Total Neto</Typography>
                  <Typography variant="h6" fontWeight="bold">S/ {bancoPreview.totalNeto?.toFixed(2)}</Typography>
                </Paper>
              </Box>

              {/* Advertencias */}
              {bancoPreview.advertencias?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>{bancoPreview.advertencias.length} trabajador(es) sin cuenta bancaria:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {bancoPreview.advertencias.slice(0, 5).map((a: string, i: number) => (
                      <li key={i}>{a}</li>
                    ))}
                    {bancoPreview.advertencias.length > 5 && <li>... y {bancoPreview.advertencias.length - 5} más</li>}
                  </ul>
                </Alert>
              )}

              {/* Configuración */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Banco</InputLabel>
                  <Select value={bancoSelected} label="Banco" onChange={(e) => setBancoSelected(e.target.value)}>
                    <MenuItem value="BCP">BCP (Telecrédito)</MenuItem>
                    <MenuItem value="BBVA">BBVA Continental</MenuItem>
                    <MenuItem value="INTERBANK">Interbank</MenuItem>
                    <MenuItem value="SCOTIABANK">Scotiabank</MenuItem>
                  </Select>
                </FormControl>
                {bancoSelected === 'BCP' && (
                  <TextField label="Cuenta Cargo (empresa)" size="small" fullWidth
                    value={bancoCuentaCargo}
                    onChange={(e) => setBancoCuentaCargo(e.target.value)}
                    placeholder="Ej: 1910695055056"
                    helperText="Cuenta corriente de la empresa en BCP" />
                )}
              </Box>

              {bancoSelected === 'BCP' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Formato BCP Telecrédito: archivo TXT de ancho fijo con registros tipo 1 (cabecera) y tipo 2 (detalle por trabajador).
                </Alert>
              )}
              {bancoSelected !== 'BCP' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Formato genérico CSV con columnas: TipoDocumento, NumeroDocumento, Nombre, NumeroCuenta, CCI, Moneda, Monto. Puede adaptarse al formato específico del banco.
                </Alert>
              )}

              {/* Tabla preview */}
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Trabajador</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>Banco</TableCell>
                      <TableCell>Cuenta</TableCell>
                      <TableCell align="right">Neto S/</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bancoPreview.detalles?.map((d: any) => (
                      <TableRow key={d.idTrabajador} sx={!d.numeroCuenta ? { bgcolor: '#fff3e0' } : {}}>
                        <TableCell>{d.nombre}</TableCell>
                        <TableCell>{d.numeroDocumento}</TableCell>
                        <TableCell>{d.banco || '-'}</TableCell>
                        <TableCell>{d.numeroCuenta || 'SIN CUENTA'}</TableCell>
                        <TableCell align="right">{d.netoPagar?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity="error" sx={{ mt: 1 }}>Error al cargar datos de la planilla</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBancoDialogOpen(false)}>Cerrar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleGenerarArchivoBanco}
            disabled={bancoGenerando || !bancoPreview || bancoPreview.trabajadoresConCuenta === 0
              || (bancoSelected === 'BCP' && !bancoCuentaCargo.trim())}
            startIcon={bancoGenerando ? <CircularProgress size={16} color="inherit" /> : <ExportIcon />}
          >
            {bancoGenerando ? 'Generando...' : `Descargar ${bancoSelected === 'BCP' ? 'TXT' : 'CSV'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanillonToolbar;
